import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconPlus, IconUsers } from '../../components/icons';
import { Avatar, Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, SearchSelect, Toggle, useAsync, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';

export function UsersPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { notify } = useToast();
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [modal, setModal] = useState(false);

  const users = useAsync(
    () => api.get('/api/users', { search, includeInactive: includeInactive ? 'true' : '' }),
    [search, includeInactive],
  );

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconUsers width={22} height={22} />}
        title="Benutzerverwaltung"
        subtitle={
          <>
            Benutzerkonten mit Rollen, Gruppenmitgliedschaften, Einzelrechten und Objektzuständigkeiten.
            Gastzugänge lassen sich zeitlich befristen – etwa für Subunternehmer.
          </>
        }
        pill="Administration"
        tone="danger"
        actions={
          can('admin.users.manage') && (
            <button className="btn primary" onClick={() => setModal(true)}>
              <IconPlus width={15} height={15} /> Benutzer anlegen
            </button>
          )
        }
      />

      <Card noBody>
        <div className="card-body tight row" style={{ gap: 12 }}>
          <input
            type="search"
            placeholder="Name, E-Mail oder Personalnummer suchen …"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ maxWidth: 360 }}
          />
          <Toggle checked={includeInactive} onChange={setIncludeInactive} label="Deaktivierte anzeigen" />
        </div>

        {users.loading ? (
          <Loading />
        ) : !users.data?.length ? (
          <EmptyState title="Keine Benutzer gefunden" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Rollen</th>
                  <th>Gruppen</th>
                  <th>Funktion</th>
                  <th>Sicherheit</th>
                  <th>Letzte Anmeldung</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {users.data.map((user: any) => (
                  <tr key={user.id} className="clickable" onClick={() => navigate(`/admin/users/${user.id}`)}>
                    <td>
                      <div className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                        <Avatar firstName={user.first_name} lastName={user.last_name} />
                        <div>
                          <strong>
                            {user.first_name} {user.last_name}
                          </strong>
                          <div className="small subtle">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 4 }}>
                        {(user.roles ?? []).map((role: any) => (
                          <Badge key={role.id} color={role.color}>
                            {role.name}
                          </Badge>
                        ))}
                        {user.is_superadmin ? <Badge variant="danger">Superadmin</Badge> : null}
                        {(user.roles ?? []).length === 0 && !user.is_superadmin && (
                          <span className="small subtle">keine</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: 4 }}>
                        {(user.groups ?? []).slice(0, 2).map((group: any) => (
                          <Badge key={group.id}>{group.name}</Badge>
                        ))}
                        {(user.groups ?? []).length > 2 && (
                          <span className="small subtle">+{user.groups.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="small">{user.job_title ?? '–'}</td>
                    <td>
                      {user.two_factor_enabled ? (
                        <Badge variant="success">2FA</Badge>
                      ) : (
                        <span className="small subtle">–</span>
                      )}
                    </td>
                    <td className="small subtle">{user.last_login_at ? formatDateTime(user.last_login_at) : 'nie'}</td>
                    <td>
                      {!user.is_active ? (
                        <Badge variant="danger">Deaktiviert</Badge>
                      ) : user.is_guest ? (
                        <Badge variant="warning">
                          Gast{user.valid_until ? ` bis ${formatDateTime(user.valid_until).slice(0, 10)}` : ''}
                        </Badge>
                      ) : (
                        <Badge variant="success">Aktiv</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modal && (
        <NewUserModal
          onClose={() => setModal(false)}
          onSaved={(password) => {
            setModal(false);
            users.reload();
            notify(
              password ? `Benutzer angelegt. Initialpasswort: ${password}` : 'Benutzer angelegt.',
              'success',
            );
          }}
        />
      )}
    </div>
  );
}

function NewUserModal({ onClose, onSaved }: { onClose: () => void; onSaved: (password?: string) => void }) {
  const { user: me } = useAuth();
  const roles = useAsync(() => api.get('/api/roles').catch(() => []), []);
  const groups = useAsync(() => api.get('/api/groups').catch(() => []), []);
  const properties = useAsync(() => api.get('/api/properties').catch(() => []), []);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    employeeNo: '',
    phone: '',
    password: '',
    isGuest: false,
    validUntil: '',
    mustChangePassword: true,
  });
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [propertyIds, setPropertyIds] = useState<number[]>([]);
  const [error, setError] = useState<unknown>(null);

  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));

  const toggleIn = (list: number[], setList: (value: number[]) => void, id: number) =>
    setList(list.includes(id) ? list.filter((entry) => entry !== id) : [...list, id]);

  return (
    <Modal
      title="Benutzer anlegen"
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!form.email || !form.firstName || !form.lastName}
            onClick={async () => {
              try {
                const result = await api.post('/api/users', {
                  ...form,
                  password: form.password || undefined,
                  validUntil: form.validUntil ? `${form.validUntil} 23:59:59` : undefined,
                  roleIds,
                  groupIds,
                  propertyIds,
                });
                onSaved(result.initialPassword);
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Anlegen
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />

        <div className="form-grid">
          <Field label="Vorname" required>
            <input value={form.firstName} onChange={(event) => set('firstName', event.target.value)} autoFocus />
          </Field>
          <Field label="Nachname" required>
            <input value={form.lastName} onChange={(event) => set('lastName', event.target.value)} />
          </Field>
          <Field label="E-Mail" required>
            <input type="email" value={form.email} onChange={(event) => set('email', event.target.value)} />
          </Field>
          <Field label="Funktion">
            <input value={form.jobTitle} onChange={(event) => set('jobTitle', event.target.value)} />
          </Field>
          <Field label="Personalnummer">
            <input value={form.employeeNo} onChange={(event) => set('employeeNo', event.target.value)} />
          </Field>
          <Field label="Telefon">
            <input value={form.phone} onChange={(event) => set('phone', event.target.value)} />
          </Field>
          <Field label="Initialpasswort" hint="Leer lassen, um ein sicheres Passwort erzeugen zu lassen.">
            <input type="text" value={form.password} onChange={(event) => set('password', event.target.value)} />
          </Field>
          {form.isGuest && (
            <Field label="Zugang gültig bis" required>
              <input type="date" value={form.validUntil} onChange={(event) => set('validUntil', event.target.value)} />
            </Field>
          )}
        </div>

        <div className="row" style={{ gap: 20 }}>
          <Toggle checked={form.mustChangePassword} onChange={(value) => set('mustChangePassword', value)} label="Passwortwechsel bei erster Anmeldung" />
          <Toggle checked={form.isGuest} onChange={(value) => set('isGuest', value)} label="Externer Gastzugang (befristet)" />
        </div>

        <Field label="Rollen" hint="Rollen bündeln Rechte. Ein Benutzer kann mehrere Rollen besitzen.">
          <div className="row" style={{ gap: 6 }}>
            {(roles.data ?? []).map((role: any) => (
              <button
                key={role.id}
                type="button"
                className={`btn sm ${roleIds.includes(role.id) ? 'active' : ''}`}
                onClick={() => toggleIn(roleIds, setRoleIds, role.id)}
                style={roleIds.includes(role.id) ? { borderColor: role.color, color: role.color } : undefined}
              >
                {role.name}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Gruppen" hint="Gruppen vererben Rollen und Objektzuständigkeiten – auch an Untergruppen.">
          <div className="row" style={{ gap: 6 }}>
            {(groups.data ?? []).map((group: any) => (
              <button
                key={group.id}
                type="button"
                className={`btn sm ${groupIds.includes(group.id) ? 'active' : ''}`}
                onClick={() => toggleIn(groupIds, setGroupIds, group.id)}
              >
                {group.name}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Objektzuständigkeiten" hint="Wirkt zusammen mit dem Scope „Meine Objekte“.">
          <div className="row" style={{ gap: 6 }}>
            {(properties.data ?? []).map((property: any) => (
              <button
                key={property.id}
                type="button"
                className={`btn sm ${propertyIds.includes(property.id) ? 'active' : ''}`}
                onClick={() => toggleIn(propertyIds, setPropertyIds, property.id)}
              >
                {property.name}
              </button>
            ))}
          </div>
        </Field>
      </div>
    </Modal>
  );
}
